<template>
  <b-modal
    v-model="modalVisible"
    :title="$t('poster.ai_modal_title')"
    size="lg"
    scrollable
    @hidden="resetForm"
  >
    <error-alert
      v-if="error"
      :error="error"
      class="mb-2"
    />

    <b-form @submit.prevent="onSubmit">
      <b-form-group
        :label="$t('poster.ai_platform')"
        label-for="ai-platform"
      >
        <b-form-select
          id="ai-platform"
          v-model="form.platform"
          :options="platformOptions"
          text-field="text"
          value-field="value"
        />
      </b-form-group>

      <b-form-group
        :label="$t('poster.ai_product_description')"
        label-for="ai-product"
      >
        <b-form-textarea
          id="ai-product"
          v-model="form.product_description"
          rows="4"
          required
          :placeholder="$t('poster.ai_product_placeholder')"
        />
      </b-form-group>

      <b-form-group
        :label="$t('poster.ai_target_audience')"
        label-for="ai-audience"
      >
        <b-form-input
          id="ai-audience"
          v-model="form.target_audience"
          required
          :placeholder="$t('poster.ai_target_audience_placeholder')"
        />
      </b-form-group>

      <b-form-group :label="$t('poster.ai_goals')">
        <small class="text-muted d-block mb-1">{{ $t('poster.ai_goals_hint') }}</small>
        <b-form-checkbox-group
          id="ai-goals"
          v-model="form.goals"
          :options="goalCheckOptions"
          class="ai-inline-controls"
          name="ai-goals"
        />
      </b-form-group>

      <b-form-group :label="$t('poster.ai_tone')">
        <b-form-radio-group
          v-model="form.tone"
          :options="toneRadioOptions"
          class="ai-inline-controls"
          name="ai-tone"
        />
      </b-form-group>

      <b-form-group
        :label="$t('poster.ai_language')"
        label-for="ai-language"
      >
        <b-form-select
          id="ai-language"
          v-model="form.language"
          :options="languageOptions"
          text-field="text"
          value-field="value"
        />
      </b-form-group>

      <b-form-group
        :label="$t('poster.ai_length')"
        label-for="ai-length"
      >
        <b-form-select
          id="ai-length"
          v-model="form.length"
          :options="lengthOptions"
          text-field="text"
          value-field="value"
        />
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        :disabled="loading"
        @click="modalVisible = false"
      >
        {{ $t('forms.cancel') }}
      </b-button>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="onSubmit"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-1"
        />
        {{ $t('poster.ai_generate_submit') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormTextarea,
  BFormCheckboxGroup,
  BFormRadioGroup,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import ErrorAlert from '@/@bya3/components/alerts/ErrorAlert'
import CoreAiApi from '@/api/core-ai'

const defaultFormFields = () => ({
  product_description: '',
  target_audience: '',
  goals: ['بيع'],
  tone: 'ودية',
  language: 'عربي',
  length: 'متوسط',
})

export default {
  name: 'PosterAiGenerateModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormTextarea,
    BFormCheckboxGroup,
    BFormRadioGroup,
    BSpinner,
    BButton,
    ErrorAlert,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    /** Pre-select platform when opening from FB vs Instagram poster */
    defaultPlatform: {
      type: String,
      default: 'Facebook',
      validator: v => v === 'Facebook' || v === 'Instagram',
    },
  },
  data() {
    return {
      form: {
        ...defaultFormFields(),
        platform: this.defaultPlatform === 'Instagram' ? 'Instagram' : 'Facebook',
      },
      loading: false,
      error: null,
    }
  },
  computed: {
    modalVisible: {
      get() {
        return this.show
      },
      set(v) {
        this.$emit('update:show', v)
      },
    },
    platformOptions() {
      return [
        { value: 'Facebook', text: this.$t('poster.ai_platform_facebook') },
        { value: 'Instagram', text: this.$t('poster.ai_platform_instagram') },
      ]
    },
    goalCheckOptions() {
      return [
        { value: 'بيع', text: this.$t('poster.ai_goal_sale') },
        { value: 'تفاعل', text: this.$t('poster.ai_goal_engagement') },
        { value: 'توعية', text: this.$t('poster.ai_goal_awareness') },
        { value: 'زيادة المتابعين', text: this.$t('poster.ai_goal_followers') },
        { value: 'زيادة الزيارات', text: this.$t('poster.ai_goal_traffic') },
        { value: 'استقطاب عملاء', text: this.$t('poster.ai_goal_leads') },
        { value: 'بناء الثقة', text: this.$t('poster.ai_goal_trust') },
        { value: 'تعزيز الولاء', text: this.$t('poster.ai_goal_loyalty') },
        { value: 'ترويج حدث', text: this.$t('poster.ai_goal_event') },
        { value: 'دعم العملاء', text: this.$t('poster.ai_goal_support') },
        { value: 'تعريف بالعلامة', text: this.$t('poster.ai_goal_brand_intro') },
        { value: 'جمع آراء', text: this.$t('poster.ai_goal_feedback') },
      ]
    },
    toneRadioOptions() {
      return [
        { value: 'رسمية', text: this.$t('poster.ai_tone_formal') },
        { value: 'ودية', text: this.$t('poster.ai_tone_friendly') },
        { value: 'حماسية', text: this.$t('poster.ai_tone_excited') },
        { value: 'فكاهية', text: this.$t('poster.ai_tone_humorous') },
        { value: 'إلهامية', text: this.$t('poster.ai_tone_inspirational') },
        { value: 'احترافية', text: this.$t('poster.ai_tone_professional') },
        { value: 'عاجلة', text: this.$t('poster.ai_tone_urgent') },
        { value: 'بسيطة', text: this.$t('poster.ai_tone_simple') },
        { value: 'تحفيزية', text: this.$t('poster.ai_tone_motivational') },
        { value: 'عاطفية', text: this.$t('poster.ai_tone_emotional') },
      ]
    },
    languageOptions() {
      return [
        { value: 'عربي', text: this.$t('poster.ai_language_ar') },
        { value: 'إنجليزي', text: this.$t('poster.ai_language_en') },
        { value: 'خليط', text: this.$t('poster.ai_language_mixed') },
      ]
    },
    lengthOptions() {
      return [
        { value: 'قصير', text: this.$t('poster.ai_length_short') },
        { value: 'متوسط', text: this.$t('poster.ai_length_medium') },
        { value: 'طويل', text: this.$t('poster.ai_length_long') },
      ]
    },
  },
  watch: {
    defaultPlatform() {
      this.form.platform = this.defaultPlatform === 'Instagram' ? 'Instagram' : 'Facebook'
    },
  },
  methods: {
    resetForm() {
      this.form = {
        ...defaultFormFields(),
        platform: this.defaultPlatform === 'Instagram' ? 'Instagram' : 'Facebook',
      }
      this.error = null
      this.loading = false
    },
    async onSubmit() {
      this.error = null
      if (!this.form.product_description?.trim() || !this.form.target_audience?.trim()) {
        this.error = this.$tc('errors.field-invalid', 1)
        return
      }
      if (!Array.isArray(this.form.goals) || this.form.goals.length < 1) {
        this.error = this.$t('poster.ai_goals_required')
        return
      }
      this.loading = true
      try {
        const { text } = await CoreAiApi.generateSocialMediaPost({
          platform: this.form.platform,
          product_description: this.form.product_description.trim(),
          target_audience: this.form.target_audience.trim(),
          goals: [...this.form.goals],
          tone: this.form.tone,
          language: this.form.language,
          length: this.form.length,
        })
        this.$emit('generated', text)
        this.modalVisible = false
        this.$notifySuccess(this.$t('poster.ai_generated_success'))
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ai-inline-controls ::v-deep .custom-control {
  display: inline-flex;
  align-items: center;
  margin-right: 1rem;
  margin-bottom: 0.35rem;
  margin-top: 0.3rem;
}
</style>
